import React, { useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../../asset/project/default_1.png';
import { IndexOrderData } from '../../../shared/dto/EntityRepository';
import { shrinkString } from '../../../shared/util/shrinkString';
import { useOptimizedImageUrl } from '../../../shared/hooks/useOptimizedImageUrl';

// Constants
const MAX_MAIN_TEXT_LENGTH = 24; // 모바일 최적화: 더 짧은 텍스트 길이
const HANDLE_ICON = '::';
const resolveOriginUrl = (originUrl: IndexOrderData['originUrl']) =>
  typeof originUrl === 'string' ? originUrl : originUrl?.src;

// Types
interface IndexCardProps {
  data: IndexOrderData;
  isDragging: boolean;
  onDragStart: (data: IndexOrderData) => void;
  onDragEnd: () => void;
}

const IndexCardMobile: React.FC<IndexCardProps> = ({
  data,
  isDragging,
  onDragStart,
  onDragEnd,
}) => {
  const handleDragStart = () => onDragStart(data);
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const originUrl = resolveOriginUrl(data.originUrl);
  const imageUrl = useOptimizedImageUrl(originUrl ?? '', thumbContainerRef);

  return (
    <CardContainer
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      $isDragging={isDragging}
    >
      <LeftPart ref={thumbContainerRef}>
        <DragHandle>{HANDLE_ICON}</DragHandle>
        {originUrl !== undefined && (
          <ThumbImg
            src={imageUrl === '' ? defaultImg : imageUrl}
            alt=""
            $isPdf={data.subText === 'PDF'}
          />
        )}
      </LeftPart>
      <ContentContainer>
        <MainText>
          {shrinkString(data.mainText, MAX_MAIN_TEXT_LENGTH)}
        </MainText>
        <SubText>{data.subText}</SubText>
      </ContentContainer>
    </CardContainer>
  );
};

// Styled Components
const CardContainer = styled.div<{ $isDragging: boolean }>`
  width: 100%;
  height: ${({ $isDragging }) => $isDragging ? '10px' : '40px'};
  min-height: ${({ $isDragging }) => $isDragging ? '10px' : '40px'};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleY(0.2) scaleX(0.5)' : 'scaleY(1) scaleX(1)'
  };
  opacity: ${({ $isDragging }) => $isDragging ? '0.5' : '1'};

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 10px;

  box-shadow: inset 1px 1px 3px 0 rgba(255, 255, 255, 0.35), inset -1px -1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 1px;
  transition: all 0.4s ease-out;

  /* 모바일 최적화: 스크롤과 드래그 충돌 방지 */
  touch-action: ${({ $isDragging }) => $isDragging ? 'none' : 'pan-y'};
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

const LeftPart = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DragHandle = styled.div`
  ${({ theme }) => theme.typography.Body_03_1};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbImg = styled.img <{ $isPdf: boolean }>`
  width: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  height: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  min-width: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  min-height: ${({ $isPdf }) => $isPdf ? '20px' : '24px'};
  object-fit: contain;
  border-radius: 2px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
  gap: 4px; /* 모바일 최적화: 간격 증가 */
`;

const MainText = styled.div`
  ${({ theme }) => theme.typography.Body_04};
`;

const SubText = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

export default IndexCardMobile;